import React from "react";
import { Link, useParams } from "react-router-dom";
import { Overline, Section } from "../components/UI";
import { SEO } from "../components/SEO";
import { buildCanonical } from "../lib/siteOrigin";
import { ArrowLeft } from "lucide-react";
import { APPROVED_ARTICLES } from "../data/approvedArticles";
import { DerangedDopplerFGR } from "./articles/DerangedDopplerFGR";
import { HyperthyroidismPregnancyFetalThyroid } from "./articles/HyperthyroidismPregnancyFetalThyroid";
import { GallstonesPregnancySurgery } from "./articles/GallstonesPregnancySurgery";
import { ChronicKidneyDiseasePregnancyPreeclampsia } from "./articles/ChronicKidneyDiseasePregnancyPreeclampsia";
import { TorchIggIgmPositivePregnancy } from "./articles/TorchIggIgmPositivePregnancy";
import { PsoriasisPregnancyTreatmentBiologics } from "./articles/PsoriasisPregnancyTreatmentBiologics";
import { EpilepsyPregnancySeizureMedicine } from "./articles/EpilepsyPregnancySeizureMedicine";
import { OralThrushPregnancyCandidiasis } from "./articles/OralThrushPregnancyCandidiasis";
import { HepatitisBPositivePartnerPregnancy } from "./articles/HepatitisBPositivePartnerPregnancy";
import { ObesityPregnancyHeparinInjections } from "./articles/ObesityPregnancyHeparinInjections";

// Registry of article body components, keyed by slug.
// Every approved Article must have an entry here.
const ARTICLE_BODIES = {
  "deranged-doppler-fetal-growth-restriction": DerangedDopplerFGR,
  "hyperthyroidism-pregnancy-fetal-thyroid": HyperthyroidismPregnancyFetalThyroid,
  "gallstones-pregnancy-surgery": GallstonesPregnancySurgery,
  "chronic-kidney-disease-pregnancy-preeclampsia": ChronicKidneyDiseasePregnancyPreeclampsia,
  "torch-igg-igm-positive-pregnancy": TorchIggIgmPositivePregnancy,
  "psoriasis-pregnancy-treatment-biologics": PsoriasisPregnancyTreatmentBiologics,
  "epilepsy-pregnancy-seizure-medicine": EpilepsyPregnancySeizureMedicine,
  "oral-thrush-pregnancy-candidiasis": OralThrushPregnancyCandidiasis,
  "hepatitis-b-positive-partner-pregnancy": HepatitisBPositivePartnerPregnancy,
  "obesity-pregnancy-heparin-injections": ObesityPregnancyHeparinInjections,
};

export function ArticlesHub() {
  return (
    <Section>
      <SEO
        title="High-Risk Pregnancy Articles | Dr Tania Gurdip Singh"
        description="Evidence-based articles explaining how pregnancy can change the interpretation of a diagnosis, the safety of treatment, and the timing of medical decisions."
        breadcrumbs={[{ name: "Home", item: "/" }, { name: "Articles", item: "/articles" }]}
      />
      <Overline>Articles</Overline>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-[54px] text-navy mt-3 tracking-tight leading-[1.05] max-w-4xl">
        When pregnancy changes the answer.
      </h1>
      <p className="text-lg text-stone2 mt-5 max-w-[860px] leading-relaxed">
        Evidence-based articles on how pregnancy can change the meaning of a diagnosis, the safety of a treatment, and the timing of the next decision.
      </p>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-gallery">
        {APPROVED_ARTICLES.map((a) => (
          <Link
            key={a.slug}
            to={`/articles/${a.slug}`}
            data-testid={a.testid}
            className="group block bg-white border border-line p-6 hover:shadow-md transition-shadow"
          >
            <Overline>{a.category}</Overline>
            <h3 className="font-serif text-2xl text-navy mt-2 leading-tight group-hover:underline underline-offset-4">
              {a.title}
            </h3>
            <p className="text-sm text-stone2 mt-3 leading-relaxed">{a.cardDescription}</p>
            <div className="mt-4 mono text-xs text-stone2">
              {a.readingTimeMinutes} min read
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

export function ArticleDetail() {
  const { slug } = useParams();
  const meta = APPROVED_ARTICLES.find((a) => a.slug === slug);
  const Body = ARTICLE_BODIES[slug];

  if (!meta || !Body) {
    return (
      <Section>
        <SEO title="Article not found" noindex />
        <p className="text-stone2">Article not found.</p>
        <Link
          to="/articles"
          className="mono text-xs uppercase tracking-widest text-navy hover:underline inline-flex items-center gap-1.5 mt-4"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All articles
        </Link>
      </Section>
    );
  }

  const canonical = buildCanonical(`/articles/${meta.slug}`);

  const medicalWebPageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    headline: meta.title,
    description: meta.metaDescription,
    url: canonical,
    inLanguage: "en",
    author: {
      "@type": "Person",
      name: meta.author,
    },
    about: {
      "@type": "MedicalCondition",
      name: meta.aboutCondition || meta.title,
    },
  };

  return (
    <Section>
      <SEO
        title={meta.seoTitle}
        description={meta.metaDescription}
        type="article"
        canonical={canonical}
        jsonLd={medicalWebPageSchema}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Articles", item: "/articles" },
          { name: meta.title, item: `/articles/${meta.slug}` },
        ]}
      />
      <div className="max-w-3xl mx-auto">
        <Link
          to="/articles"
          className="mono text-xs uppercase tracking-widest text-stone2 hover:text-navy inline-flex items-center gap-1.5"
          data-testid="article-back-to-articles"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All articles
        </Link>
        <div className="mt-6">
          <Overline>{meta.category}</Overline>
        </div>
        <h1
          className="font-serif text-4xl md:text-5xl text-navy mt-3 tracking-tight leading-[1.1]"
          data-testid="article-heading"
        >
          {meta.title}
        </h1>
        <div className="mt-4 mono text-xs text-stone2">
          {meta.readingTimeMinutes} min read
        </div>

        <Body />
      </div>
    </Section>
  );
}
